/**
 * The assembled catalog: collections, id indexes and lookups.
 *
 * Everything here is built once at module load from the static data files.
 * There is no IO, no fetch and no async — the catalog ships *as code* so the
 * recipe editor, the Worker and the test suite all read the identical dataset
 * (PLAN §6).
 */

import { equipmentPresets } from './data/equipment/index';
import { fermentables } from './data/fermentables/index';
import { hops } from './data/hops/index';
import { miscs } from './data/misc/index';
import { styles } from './data/styles/index';
import { suggestTargetForCategory, waterTargetPresetsById } from './data/water-targets/index';
import { yeastFamilies } from './data/yeast/families';
import { yeasts } from './data/yeast/index';
import type { EquipmentPreset } from './schema/equipment';
import type { Fermentable } from './schema/fermentable';
import type { Hop } from './schema/hop';
import type { Misc } from './schema/misc';
import type { Style } from './schema/style';
import type { WaterTargetPreset } from './schema/water-target';
import type { Yeast } from './schema/yeast';
import type { YeastFamily } from './schema/yeast-family';

export { CLASS_ALIASES, classAliases } from './data/fermentables/class-names';
export { fermentables } from './data/fermentables/index';
export {
  MALTSTER_REACH,
  type MaltsterReach,
  makerReach,
} from './data/fermentables/makers';
export { hops } from './data/hops/index';
export { miscs } from './data/misc/index';
export { styles } from './data/styles/index';
export { yeastFamilies } from './data/yeast/families';
export { yeasts } from './data/yeast/index';

// ---------------------------------------------------------------------------
// Kinds
// ---------------------------------------------------------------------------

/** The five things the catalog holds. */
export type CatalogKind = 'fermentable' | 'hop' | 'yeast' | 'misc' | 'style';

/** Maps a {@link CatalogKind} onto the entry type it yields. */
export interface CatalogEntryByKind {
  fermentable: Fermentable;
  hop: Hop;
  yeast: Yeast;
  misc: Misc;
  style: Style;
}

/** Any catalog entry. */
export type CatalogEntry = CatalogEntryByKind[CatalogKind];

/** Every kind, in the order the UI lists them. */
export const CATALOG_KINDS = ['fermentable', 'hop', 'yeast', 'misc', 'style'] as const;

/** Every collection, keyed by kind. */
export const collections: { readonly [K in CatalogKind]: readonly CatalogEntryByKind[K][] } = {
  fermentable: fermentables,
  hop: hops,
  yeast: yeasts,
  misc: miscs,
  style: styles,
};

// ---------------------------------------------------------------------------
// Indexes
// ---------------------------------------------------------------------------

function indexById<T extends { readonly id: string }>(
  entries: readonly T[],
): ReadonlyMap<string, T> {
  return new Map(entries.map((entry) => [entry.id, entry]));
}

/** `id` → fermentable. */
export const fermentablesById = indexById(fermentables);
/** `id` → hop. */
export const hopsById = indexById(hops);
/** `id` → yeast. */
export const yeastsById = indexById(yeasts);
/** `id` → misc addition. */
export const miscsById = indexById(miscs);
/** `id` → style. */
export const stylesById = indexById(styles);

const indexes: { readonly [K in CatalogKind]: ReadonlyMap<string, CatalogEntryByKind[K]> } = {
  fermentable: fermentablesById,
  hop: hopsById,
  yeast: yeastsById,
  misc: miscsById,
  style: stylesById,
};

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

/**
 * Look an entry up by kind and id.
 *
 * Returns `undefined` rather than throwing: the AI assistant's
 * `search_catalog` path (PLAN §7.1) needs a miss to be an ordinary answer, and
 * the editor needs to survive a recipe that references a retired id.
 *
 * @param kind - Which collection to search.
 * @param id - The stable catalog id.
 */
export function getById<K extends CatalogKind>(
  kind: K,
  id: string,
): CatalogEntryByKind[K] | undefined {
  const index = indexes[kind] as ReadonlyMap<string, CatalogEntryByKind[K]>;
  return index.get(id);
}

/** {@link getById} for fermentables. */
export function getFermentable(id: string): Fermentable | undefined {
  return fermentablesById.get(id);
}

/** {@link getById} for hops. */
export function getHop(id: string): Hop | undefined {
  return hopsById.get(id);
}

/** {@link getById} for yeast. */
export function getYeast(id: string): Yeast | undefined {
  return yeastsById.get(id);
}

/** {@link getById} for misc additions. */
export function getMisc(id: string): Misc | undefined {
  return miscsById.get(id);
}

/** {@link getById} for styles. */
export function getStyle(id: string): Style | undefined {
  return stylesById.get(id);
}

/**
 * Resolve a hop's `substitutes` to the hops themselves.
 *
 * Every id in the catalog resolves (the test suite enforces it), so an empty
 * result means the hop genuinely lists no substitutes.
 *
 * @param hop - The hop whose substitutes to resolve.
 */
export function resolveSubstitutes(hop: Hop): readonly Hop[] {
  const resolved: Hop[] = [];
  for (const id of hop.substitutes) {
    const match = hopsById.get(id);
    if (match) resolved.push(match);
  }
  return resolved;
}

/**
 * Other origin lots of the same cultivar.
 *
 * Empty when `cultivar` is null (unique variety, or a related-but-different
 * plant such as Sterling). The canonical landrace is included for variants
 * and variants are included for the landrace — every family member except
 * the hop you passed in. The landrace (`id === cultivar`) sorts first.
 *
 * @param hop - The hop whose origin lots to resolve.
 */
export function resolveCultivarSiblings(hop: Hop): readonly Hop[] {
  if (hop.cultivar === null) return [];
  const cultivar = hop.cultivar;
  const siblings = hops.filter(
    (candidate) => candidate.cultivar === cultivar && candidate.id !== hop.id,
  );
  return siblings.sort((a, b) => {
    if (a.id === cultivar) return -1;
    if (b.id === cultivar) return 1;
    return a.name.localeCompare(b.name);
  });
}

/**
 * `yeast id` → the family it belongs to.
 *
 * Built once, from the families rather than from the yeasts, which is the whole
 * point of a shared record: a member cannot see a sibling that does not see it
 * back, because there is only one list and both readings come from it. The hop
 * equivalent (`cultivar`) gets the same guarantee by having every sibling name
 * the same slug; this gets it by having one place to name them all.
 */
const yeastFamilyByMember: ReadonlyMap<string, YeastFamily> = new Map(
  yeastFamilies.flatMap((family) => family.members.map((id) => [id, family] as const)),
);

/**
 * The cross-lab family a yeast belongs to, if any.
 *
 * `undefined` for most of the catalog and that is the expected answer, not a
 * gap: a family is only authored where the equivalence is defensible, so the
 * absence of one means nothing has been claimed rather than that nothing is
 * known.
 *
 * @param yeast - The yeast whose family to look up.
 */
export function yeastFamilyFor(yeast: Yeast): YeastFamily | undefined {
  return yeastFamilyByMember.get(yeast.id);
}

/**
 * The other labs' cuts of the same strain.
 *
 * Every member id resolves (`yeast-families.test.ts` enforces it), so an empty
 * result means the yeast genuinely belongs to no family — the same contract
 * {@link resolveSubstitutes} keeps. The yeast itself is never in its own
 * result. Sorted by name so a panel is stable across releases; the family's
 * authored order is a reading order for the source file, not a ranking.
 *
 * @param yeast - The yeast whose equivalents to resolve.
 */
export function resolveYeastEquivalents(yeast: Yeast): readonly Yeast[] {
  const family = yeastFamilyByMember.get(yeast.id);
  if (family === undefined) return [];
  const siblings: Yeast[] = [];
  for (const id of family.members) {
    if (id === yeast.id) continue;
    const match = yeastsById.get(id);
    if (match) siblings.push(match);
  }
  return siblings.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Growing district when we know it, otherwise the country.
 *
 * The catalog table and type-ahead use this so Michigan Saaz and Yakima Saaz
 * do not both collapse to "United States".
 */
export function hopOriginLabel(hop: Hop): string {
  return hop.growingRegion ?? hop.origin;
}

/** Total number of entries across every collection. */
export function catalogSize(): number {
  return fermentables.length + hops.length + yeasts.length + miscs.length + styles.length;
}

// ---------------------------------------------------------------------------
// Presets (PLAN §4.3, §5.7 — M4)
// ---------------------------------------------------------------------------

/**
 * Equipment and water-target presets are **not** {@link CatalogKind}s, and that
 * is deliberate.
 *
 * The five kinds above are *ingredients and styles*: they are snapshotted into
 * recipes, they are what `searchCatalog` ranks, and they are what the
 * assistant's `search_catalog` tool returns. A preset is neither — it is a
 * template a user copies once to create their own document, after which the
 * catalog entry has no further relationship with it. Folding presets into
 * `CATALOG_KINDS` would put "Grainfather G30" in the ⌘K ingredient search and
 * imply a snapshot contract that does not exist, so they ship as their own
 * collections with their own getters.
 */

export { equipmentPresets } from './data/equipment/index';
export {
  DEFAULT_WATER_TARGET_ID,
  STYLE_CATEGORY_WATER_TARGETS,
  suggestTargetForCategory,
  waterTargetPresets,
  waterTargetPresetsById,
} from './data/water-targets/index';

/** `id` → equipment preset. */
export const equipmentPresetsById = indexById(equipmentPresets);

/** {@link getById} for equipment presets. Returns `undefined` on a miss. */
export function getEquipmentPreset(id: string): EquipmentPreset | undefined {
  return equipmentPresetsById.get(id);
}

/** {@link getById} for water target presets. Returns `undefined` on a miss. */
export function getWaterTargetPreset(id: string): WaterTargetPreset | undefined {
  return waterTargetPresetsById.get(id);
}

/**
 * The water target a style suggests (PLAN §4.3, "style-suggested targets").
 *
 * A **suggestion, not a prescription**: it is the documented category lookup in
 * {@link STYLE_CATEGORY_WATER_TARGETS}, and the recipe editor labels it as a
 * suggestion for exactly that reason.
 *
 * @param styleId - A catalog style id such as `bjcp-2021-21a`.
 * @returns The suggested preset, or `undefined` when the style id is unknown —
 *   a miss is an ordinary answer here, exactly as it is for {@link getStyle}.
 */
export function suggestTargetsForStyle(styleId: string): WaterTargetPreset | undefined {
  const style = stylesById.get(styleId);
  return style === undefined ? undefined : suggestTargetForCategory(style.categoryNumber);
}
